<script setup>
import { computed, onMounted } from "vue"
import VueDatePicker from "@vuepic/vue-datepicker"
import { es } from "date-fns/locale"

import HotelListaInput from "@/components/HotelListaInput.vue"
import MensajeWhatsapp from "@/components/MensajeWhatsapp.vue"
import { useCotizadorManual } from "@/composables/useCotizadorManual"
import { useAuth } from "@/composables/useAuth"

const { user, loadUser } = useAuth()

// Esta ruta no tiene meta.requiresAdmin, así que el guard global del router
// nunca llama loadUser() acá -- sin esto, si es la primera pantalla que
// carga tras el login, user.value llega null y el título se queda en el
// fallback "Cotizador" sin el nombre de la empresa. force=true: si la
// pestaña ya estaba abierta con un user.value cacheado de antes de que /me
// devolviera companySlug, igual lo vuelve a pedir.
onMounted(() => loadUser(true))

// El slug puede llevar guiones como separador (ej. "Mena-Tour"); para
// mostrarlo como título se reemplazan por espacios ("Mena Tour").
const tituloCotizador = computed(() => {
  const slug = user.value?.companySlug
  return slug ? `Cotizador Manual ${slug.replace(/-/g, " ")}` : "Cotizador Manual"
})

const {
  destinosManual,
  destinoManual,
  config,

  destinoNombre,
  inicio,
  fin,
  pax,
  trm,
  vuelos,
  traslados,

  hotelesPorGrupo,
  hotelesGrupoA,
  hotelesGrupoB,

  inicioPlan,
  vueloNeto,
  nochesAdicionales,

  fechaSalida,
  cabinas,

  habitaciones,
  hotelesDesayuno,
  hotelesTISeleccionados,
  validacionesHabitaciones,

  planCosta,
  hotelesCosta,

  resultadoTour,
  mensajePrincipal,
  mensajeSegundo,

  agregarHotel,
  eliminarHotel,
  agregarHabitacion,
  eliminarHabitacion
} = useCotizadorManual()

const tipo = computed(() => config.value?.tipo)

const esFormularioBasico = computed(() =>
  ["estandar", "combinado", "gruposTravel", "otrosUsd", "sanAndres", "costa"].includes(tipo.value)
)

const vuelosFormateado = computed({
  get: () => (vuelos.value ? new Intl.NumberFormat("es-CO").format(vuelos.value) : ""),
  set: v => { vuelos.value = Number(String(v).replace(/\./g, "").replace(/,/g, "")) || 0 }
})

const trasladosFormateado = computed({
  get: () => (traslados.value ? new Intl.NumberFormat("es-CO").format(traslados.value) : ""),
  set: v => { traslados.value = Number(String(v).replace(/\./g, "").replace(/,/g, "")) || 0 }
})

const vueloNetoFormateado = computed({
  get: () => (vueloNeto.value ? new Intl.NumberFormat("es-CO").format(vueloNeto.value) : ""),
  set: v => { vueloNeto.value = Number(String(v).replace(/\./g, "").replace(/,/g, "")) || 0 }
})
</script>

<template>
  <div class="container py-4" style="background-color: #f8f9fa; border-radius: 6px;">

    <!-- HEADER -->
    <div class="text-center mb-4">
      <h1 class="vip-title d-flex align-items-center justify-content-center gap-3">
        <span class="logo-vip">
          <i class="bi bi-calculator-fill plane-icon"></i>
        </span>
        <span class="title-animated">
          {{ tituloCotizador }}
        </span>
      </h1>
    </div>

    <form class="row g-3" @submit.prevent>

      <!-- SELECTOR DE PLANTILLA (hoja del Excel) -->
      <div class="col-md-6">
        <label class="form-label">Plantilla de cotización</label>
        <select v-model="destinoManual" class="form-select">
          <option v-for="d in destinosManual" :key="d.value" :value="d.value">
            {{ d.label }}
          </option>
        </select>
      </div>

      <!-- DESTINO (texto libre cuando la plantilla no lo fija) -->
      <div v-if="esFormularioBasico" class="col-md-6">
        <label class="form-label">Destino</label>
        <input
          type="text"
          class="form-control"
          v-model="destinoNombre"
          :disabled="!!config.destinoFijo"
          placeholder="Ej: Rio de Janeiro"
        >
      </div>

      <!-- NOTAS DE LA HOJA -->
      <div v-if="config.notas?.length || config.restriccion" class="col-12">
        <div class="alert alert-warning py-2 mb-0">
          <div v-if="config.restriccion"><strong>{{ config.restriccion }}</strong></div>
          <div v-for="(n, i) in config.notas" :key="i" class="small">• {{ n }}</div>
        </div>
      </div>

      <!-- ================= FORMULARIO BÁSICO ================= -->
      <template v-if="esFormularioBasico">

        <div class="col-md-3">
          <label class="form-label">Inicio del viaje</label>
          <VueDatePicker v-model="inicio" :format-locale="es" locale="es" model-type="yyyy-MM-dd"
            format="yyyy-MM-dd" auto-apply :enable-time-picker="false" :clearable="false" />
        </div>

        <div class="col-md-3">
          <label class="form-label">Fin del viaje</label>
          <VueDatePicker v-model="fin" :format-locale="es" locale="es" model-type="yyyy-MM-dd"
            format="yyyy-MM-dd" auto-apply :enable-time-picker="false" :clearable="false" />
        </div>

        <div v-if="tipo !== 'sanAndres'" class="col-md-2">
          <label class="form-label">Personas</label>
          <input type="number" class="form-control" v-model.number="pax" min="1">
          <small class="text-muted">No incluir infantes</small>
        </div>

        <div v-if="config.usaTrm" class="col-md-2">
          <label class="form-label">TRM</label>
          <input type="number" class="form-control" v-model.number="trm" min="0">
        </div>

        <div class="col-md-2">
          <label class="form-label">Vuelos (COP, neto)</label>
          <div class="input-group">
            <span class="input-group-text">$</span>
            <input type="text" class="form-control" v-model="vuelosFormateado" placeholder="0">
          </div>
        </div>

        <div v-if="config.usaTraslados" class="col-md-3">
          <label class="form-label">Traslados (COP)</label>
          <div class="input-group">
            <span class="input-group-text">$</span>
            <input type="text" class="form-control" v-model="trasladosFormateado" placeholder="0">
          </div>
        </div>
      </template>

      <!-- ============ HOTELES: ESTANDAR / GRUPOS / OTROS ============ -->
      <template v-if="['estandar', 'gruposTravel', 'otrosUsd'].includes(tipo)">
        <div class="col-12" v-for="g in config.grupos" :key="g.key">
          <HotelListaInput
            :titulo="g.label"
            :hoteles="hotelesPorGrupo[g.key] ?? []"
            :moneda="tipo === 'otrosUsd' ? 'USD' : 'COP'"
            :labelValor="tipo === 'gruposTravel' ? 'Valor Total Neto' : 'Valor Total Comisionable'"
            @agregar="agregarHotel(hotelesPorGrupo[g.key])"
            @eliminar="i => eliminarHotel(hotelesPorGrupo[g.key], i)"
          />
        </div>
      </template>

      <!-- =================== COMBINADOS =================== -->
      <template v-if="tipo === 'combinado'">
        <div class="col-12">
          <HotelListaInput
            :titulo="config.grupoA.label"
            :hoteles="hotelesGrupoA"
            @agregar="agregarHotel(hotelesGrupoA)"
            @eliminar="i => eliminarHotel(hotelesGrupoA, i)"
          />
        </div>

        <div class="col-12" v-for="g in config.grupoB" :key="g.key">
          <HotelListaInput
            :titulo="g.label"
            :hoteles="hotelesGrupoB[g.key] ?? []"
            @agregar="agregarHotel(hotelesGrupoB[g.key])"
            @eliminar="i => eliminarHotel(hotelesGrupoB[g.key], i)"
          />
        </div>
      </template>

      <!-- =================== SAN ANDRÉS =================== -->
      <template v-if="tipo === 'sanAndres'">
        <div class="col-12">
          <div class="card mb-3">
            <div class="card-header d-flex justify-content-between align-items-center">
              <strong>Habitaciones</strong>
              <button type="button" class="btn btn-sm btn-outline-primary"
                :disabled="habitaciones.length >= 10" @click="agregarHabitacion">
                <i class="bi bi-plus-lg"></i> Habitación
              </button>
            </div>
            <div class="card-body">
              <div v-for="(hab, i) in habitaciones" :key="i" class="row g-2 align-items-end mb-2">
                <div class="col-md-4">
                  <label class="form-label small mb-0">Habitación {{ i + 1 }} — Adultos</label>
                  <input type="number" class="form-control" v-model.number="hab.adultos" min="0" max="5">
                </div>
                <div class="col-md-4">
                  <label class="form-label small mb-0">Niños (2-9)</label>
                  <input type="number" class="form-control" v-model.number="hab.ninos" min="0" max="5">
                </div>
                <div class="col-md-3">
                  <span class="badge" :class="validacionesHabitaciones[i] === 'OK' ? 'bg-success' : 'bg-danger'">
                    {{ validacionesHabitaciones[i] }}
                  </span>
                </div>
                <div class="col-md-1">
                  <button type="button" class="btn btn-outline-danger w-100"
                    :disabled="habitaciones.length <= 1" @click="eliminarHabitacion(i)">
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-12">
          <HotelListaInput
            titulo="Hoteles Solo Desayuno (netos Travel Compositor)"
            :hoteles="hotelesDesayuno"
            labelValor="Valor Total Neto"
            @agregar="agregarHotel(hotelesDesayuno)"
            @eliminar="i => eliminarHotel(hotelesDesayuno, i)"
          />
        </div>

        <div class="col-12">
          <div class="card mb-3">
            <div class="card-header"><strong>Hoteles Todo Incluido (tarifario)</strong></div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-4" v-for="h in config.hotelesTI" :key="h.nombre">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" :value="h.nombre"
                      v-model="hotelesTISeleccionados" :id="'ti-' + h.nombre">
                    <label class="form-check-label small" :for="'ti-' + h.nombre">
                      {{ h.nombre }}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- =================== COSTA (SMR / CTG) =================== -->
      <template v-if="tipo === 'costa'">
        <div class="col-md-4">
          <label class="form-label">Alimentación</label>
          <select v-model="planCosta" class="form-select">
            <option v-for="p in config.planes" :key="p" :value="p">{{ p }}</option>
          </select>
        </div>

        <div class="col-12">
          <div class="card mb-3">
            <div class="card-header d-flex justify-content-between align-items-center">
              <strong>Hoteles ({{ planCosta }}) — tarifa por acomodación y noche</strong>
              <button type="button" class="btn btn-sm btn-outline-primary"
                @click="hotelesCosta.push({ nombre: '', tarifaAcomodacion: 0 })">
                <i class="bi bi-plus-lg"></i> Hotel
              </button>
            </div>
            <div class="card-body">
              <p class="small text-muted mb-2">
                La tarifa por acomodación es el TOTAL del tarifario:
                base comisionable + impuestos − base×14%. El sistema la multiplica
                por personas × noches × 1.18.
              </p>
              <div v-for="(h, i) in hotelesCosta" :key="i" class="row g-2 align-items-end mb-2">
                <div class="col-md-6">
                  <label class="form-label small mb-0">Hotel</label>
                  <input type="text" class="form-control" v-model="h.nombre">
                </div>
                <div class="col-md-5">
                  <label class="form-label small mb-0">Tarifa acomodación (COP/persona/noche)</label>
                  <input type="number" class="form-control" v-model.number="h.tarifaAcomodacion" min="0">
                </div>
                <div class="col-md-1">
                  <button type="button" class="btn btn-outline-danger w-100"
                    :disabled="hotelesCosta.length <= 1" @click="hotelesCosta.splice(i, 1)">
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- =================== TOUR (Turquía / Tailandia) =================== -->
      <template v-if="tipo === 'tour'">
        <div class="col-md-3">
          <label class="form-label">Fecha inicio del plan</label>
          <VueDatePicker v-model="inicioPlan" :format-locale="es" locale="es" model-type="yyyy-MM-dd"
            format="yyyy-MM-dd" auto-apply :enable-time-picker="false" :clearable="false" />
        </div>

        <div class="col-md-2">
          <label class="form-label">Personas</label>
          <input type="number" class="form-control" v-model.number="pax" min="1">
        </div>

        <div class="col-md-2">
          <label class="form-label">TRM</label>
          <input type="number" class="form-control" v-model.number="trm" min="0">
        </div>

        <div class="col-md-3">
          <label class="form-label">Vuelo neto total personas</label>
          <div class="input-group">
            <span class="input-group-text">$</span>
            <input type="text" class="form-control" v-model="vueloNetoFormateado" placeholder="0">
          </div>
        </div>

        <div class="col-md-2">
          <label class="form-label">Noches adicionales</label>
          <input type="number" class="form-control" v-model.number="nochesAdicionales" min="0">
          <small class="text-muted">Travel Compositor comisionable</small>
        </div>

        <div v-if="resultadoTour" class="col-12">
          <div class="alert alert-info mb-0">
            Vuelos: <strong>{{ resultadoTour.ida.toLocaleDateString("es-CO") }}</strong>
            → <strong>{{ resultadoTour.regreso.toLocaleDateString("es-CO") }}</strong>
            <span v-if="resultadoTour.interno">
              (interno: {{ resultadoTour.interno.toLocaleDateString("es-CO") }})
            </span>
          </div>
        </div>
      </template>

      <!-- =================== CRUCERO =================== -->
      <template v-if="tipo === 'crucero'">
        <div class="col-md-3">
          <label class="form-label">Fecha de salida</label>
          <VueDatePicker v-model="fechaSalida" :format-locale="es" locale="es" model-type="yyyy-MM-dd"
            format="yyyy-MM-dd" auto-apply :enable-time-picker="false" :clearable="false" />
        </div>

        <div class="col-md-2">
          <label class="form-label">Personas (1 cabina)</label>
          <input type="number" class="form-control" v-model.number="pax" min="1" max="5">
        </div>

        <div class="col-12">
          <div class="card mb-3">
            <div class="card-header"><strong>Valores de cabina (USD)</strong></div>
            <div class="card-body">
              <div class="row g-2">
                <div v-for="(c, key) in cabinas" :key="key" class="col-md-3">
                  <label class="form-label text-capitalize"><strong>{{ key }}</strong></label>
                  <div class="input-group mb-1">
                    <span class="input-group-text small">Sin licores</span>
                    <input type="number" class="form-control" v-model.number="c.sin" min="0">
                  </div>
                  <div class="input-group">
                    <span class="input-group-text small">Con licores</span>
                    <input type="number" class="form-control" v-model.number="c.con" min="0">
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

    </form>

    <!-- =================== MENSAJES GENERADOS =================== -->
    <div class="mt-4">
      <MensajeWhatsapp titulo="Mensaje 1: Cotización" :mensaje="mensajePrincipal" />
      <MensajeWhatsapp titulo="Mensaje 2: Por persona + Incluye" :mensaje="mensajeSegundo" />
    </div>

  </div>
</template>

<style>
.vip-title {
  font-weight: 800;
}

.logo-vip {
  width: 58px;
  height: 58px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 18px;
  background: linear-gradient(135deg, #0d6efd, #6ea8fe);
  box-shadow: 0 10px 30px rgba(13, 110, 253, 0.25);
  position: relative;
  overflow: hidden;
}

.logo-vip::before {
  content: "";
  position: absolute;
  width: 120%;
  height: 120%;
  background: linear-gradient(120deg, transparent, rgba(255, 255, 255, 0.35), transparent);
  transform: rotate(25deg);
  animation: shine 3s infinite;
}

.plane-icon {
  color: white;
  font-size: 1.4rem;
  z-index: 2;
}

.title-animated {
  background: linear-gradient(90deg, #0d6efd, #6ea8fe, #0d6efd);
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: gradientMove 4s linear infinite;
  letter-spacing: -0.5px;
}

@keyframes shine {
  0% { left: -150%; }
  100% { left: 150%; }
}

@keyframes gradientMove {
  0% { background-position: 0% center; }
  100% { background-position: 200% center; }
}
</style>
